import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import HowWeWork from "@/components/HowWeWork";
import CTASection from "@/components/CTASection";

const IMG = {
  founder: "https://customer-assets.emergentagent.com/job_strategy-led-2/artifacts/ykbp6snu_David%20Ball.webp",
};

const VALUES = [
  { title: "Clarity over chaos", copy: "Help clients focus on what matters." },
  { title: "Creativity with backbone", copy: "Design-led and imaginative, but always grounded in strategy." },
  { title: "Support that matters", copy: "Personal, practical and genuinely useful." },
  { title: "No fluff, no ego", copy: "Honest work, clear thinking and no overcomplication." },
  { title: "Consistency is power", copy: "Show up properly and help clients do the same." },
];

const APPROACH = [
  "Long-term relationships",
  "Honest advice",
  "Quality over volume",
  "Strategy before tactics",
  "Practical support",
  "Calm delivery",
];

const CLIENTS = [
  "Health and wellbeing",
  "Community organisations",
  "Creative businesses",
  "Startups",
  "Experts and educators",
  "Founder-led businesses",
  "Small teams",
  "Consultants",
  "Purpose-led organisations",
  "Professional services",
];

export default function About() {
  useEffect(() => {
    document.title = "About | Track Digital";
  }, []);

  return (
    <main data-testid="about-page">
      <section className="pt-32 md:pt-40 pb-14 md:pb-20">
        <div className="td-container">
          <Reveal>
            <span className="td-label block mb-6">About Track Digital</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.04] max-w-4xl">
              Clearer thinking, better systems and marketing that feels manageable.
            </h1>
            <p className="td-measure text-lg md:text-xl text-[#4a4a4a] leading-relaxed mt-8">
              Most businesses do not need more noise. They need direction. Track Digital is a boutique
              marketing agency and strategic partner. We bring clarity to your marketing, then we
              deliver it properly.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Founder story */}
      <section className="py-16 md:py-24 bg-[#fafafa]">
        <div className="td-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-5">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden max-w-md">
              <img src={IMG.founder} alt="David Ball, Founder" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-7">
            <span className="td-label block mb-6">The Founder</span>
            <h2 className="text-3xl md:text-4xl font-semibold leading-tight mb-6">Why Track Digital exists.</h2>
            <p className="text-lg text-[#4a4a4a] leading-relaxed td-measure mb-5">
              After years of watching good businesses struggle with scattered marketing, I built Track
              Digital around a simple idea: strategy first, then proper delivery.
            </p>
            <p className="text-lg text-[#4a4a4a] leading-relaxed td-measure mb-5">
              One of the things I enjoy most is getting to know the businesses I work with.
              Understanding how they operate, what they&apos;re trying to achieve and where marketing can
              genuinely make a difference means every solution is tailored to them. Sometimes that&apos;s
              social media management. Sometimes it&apos;s strategy, AI or a new website. Whatever the
              challenge, the aim is always the same: to bring clarity, structure and confidence to
              your marketing.
            </p>
            <p className="font-head text-lg font-semibold text-black mt-8">David Ball</p>
            <p className="text-[#8a8a8a]">Founder, Track Digital</p>
          </Reveal>
        </div>
      </section>

      {/* What makes Track different / values - editorial list */}
      <section className="py-16 md:py-24">
        <div className="td-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-4 lg:sticky lg:top-28 self-start">
            <span className="td-label block mb-5">Our Values</span>
            <h2 className="text-3xl md:text-4xl font-semibold leading-tight">
              What makes Track <span className="td-hl td-hl-butter">different</span>.
            </h2>
            <p className="text-[#6b6b6b] leading-relaxed mt-5">
              Five principles that shape every project, every conversation and every relationship.
            </p>
          </Reveal>
          <div className="lg:col-span-8">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 60}>
                <div className="flex items-baseline gap-5 md:gap-8 py-7 border-b border-[#e5e5e5] first:border-t">
                  <span className="font-head text-3xl md:text-4xl font-semibold text-[#7C6CF0] w-14 shrink-0">0{i + 1}</span>
                  <div>
                    <h3 className="text-xl md:text-2xl font-semibold mb-2">{v.title}</h3>
                    <p className="text-[#6b6b6b] leading-relaxed max-w-xl">{v.copy}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Approach - flowing list, pastel band */}
      <section className="py-16 md:py-24" style={{ background: "#ECE9FF" }}>
        <div className="td-container">
          <Reveal className="max-w-2xl mb-10">
            <span className="td-label block mb-5">Our Approach</span>
            <h2 className="text-black text-3xl md:text-4xl font-semibold leading-tight">
              What we value in how we work.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-12">
            {APPROACH.map((a, i) => (
              <Reveal key={a} delay={i * 60}>
                <div className="flex items-center gap-4 py-5 border-b border-black/10">
                  <span className="w-2 h-2 rounded-full bg-[#7C6CF0] shrink-0" />
                  <span className="text-black text-lg font-head font-medium">{a}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <HowWeWork />

      {/* Who we work with */}
      <section className="py-16 md:py-24">
        <div className="td-container">
          <Reveal className="max-w-2xl mb-12">
            <span className="td-label block mb-5">Who We Work With</span>
            <h2 className="text-3xl md:text-5xl font-semibold leading-[1.1] mb-6">
              From medics to makers.
            </h2>
            <p className="text-lg text-[#6b6b6b] leading-relaxed">
              We help organisations with something meaningful to say show up more clearly online.
            </p>
          </Reveal>
          <div className="flex flex-wrap gap-3">
            {CLIENTS.map((c) => (
              <span key={c} className="rounded-full border border-[#e5e5e5] px-5 py-2.5 text-sm text-[#4a4a4a]">
                {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        label="Work With Us"
        heading="Let us bring some clarity to your marketing."
        copy="Book a discovery call and we will talk through where you are and the clearest way forward."
      />
    </main>
  );
}
